import { Component, ElementRef, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { EsignOverlayService } from '../../../services/esign-overlay/esign-overlay.service';
import { PublicEsignService } from '../../../services/public-esign/public-esign.service';
import { EsignPositonComponent } from '../../../pages/esign-positon/esign-positon.component';
import { RegExpPatterns } from '../../enums/regex-pattern.enum';
import { submitForm } from '../../utils/submit-form.util';
import { RouteConstants } from '../../constants/routes.constants';
import { AuthService } from '../../../services/auth/auth.service';
import { environment } from '../../../../environments/environments';

@Component({
  selector: 'app-esign-upload-overlay',
  templateUrl: './esign-upload-overlay.component.html',
  styleUrl: './esign-upload-overlay.component.scss',
  standalone: false,
})
export class EsignUploadOverlayComponent implements OnInit, OnDestroy {

  faTimes = faTimes;
  isOpen = false;
  verifyLoader = false;
  pendingLoader = false;
  pendingDoc: any | null = null;
  showSuccessToaster = false;
  showErrorToaster = false;
  successMessage = '';
  errorMessage = '';
  esignNameControl = new FormControl('', Validators.required);
  selectedFile: File | null = null;
  uploadedFileName = '';
  uploadErrorMessage = '';
  isDragOver = false;
  allowedFileType = '.pdf';
  shareLoader = false;
  signingMode: 'just-me' | 'send-to-others' = 'just-me';
  recipientEmailControl = new FormControl('', [Validators.required, Validators.pattern(RegExpPatterns.EmailPattern)]);
  recipientEmailError = '';
  deductFromOwner = false;
  @ViewChild('file') fileInput!: ElementRef<HTMLInputElement>;
  private openSub!: Subscription;
  private resizeTimer: any;

  @HostListener('window:resize')
  onResize(): void {
    document.body.classList.add('no-transition');
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      document.body.classList.remove('no-transition');
    }, 150);
  }

  constructor(
    private readonly overlayService: EsignOverlayService,
    private readonly publicEsignService: PublicEsignService,
    private readonly modalService: NgbModal,
    private readonly sanitizer: DomSanitizer,
    private readonly router: Router,
    private readonly authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.openSub = this.overlayService.open$.subscribe((payload: any) => this.open(payload ?? null));
  }

  ngOnDestroy(): void {
    this.openSub?.unsubscribe();
    clearTimeout(this.resizeTimer);
  }

  open(payload: any = null): void {
    this.resetState();
    this.pendingDoc = payload;
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
    this.resetState();
  }

  private resetState(): void {
    this.esignNameControl.reset();
    this.selectedFile = null;
    this.uploadedFileName = '';
    this.uploadErrorMessage = '';
    this.isDragOver = false;
    this.signingMode = 'just-me';
    this.recipientEmailControl.reset();
    this.recipientEmailError = '';
    this.shareLoader = false;
    this.pendingDoc = null;
    this.pendingLoader = false;
    this.deductFromOwner = false;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  triggerFileInputClick(): void {
    this.fileInput.nativeElement.click();
  }

  upload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    this.processFile(file);
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.processFile(file);
  }

  processFile(file: File | undefined): void {
    const maxBytes = 10 * 1024 * 1024;
    if (!file) { this.uploadErrorMessage = ''; return; }

    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (ext !== this.allowedFileType) {
      this.uploadErrorMessage = 'Only PDF files are allowed.';
      this.selectedFile = null;
      this.uploadedFileName = '';
      return;
    }
    if (file.size > maxBytes) {
      this.uploadErrorMessage = 'File size exceeds the maximum limit of 10 MB.';
      this.selectedFile = null;
      this.uploadedFileName = '';
      return;
    }
    this.selectedFile = file;
    this.uploadedFileName = file.name;
    this.uploadErrorMessage = '';
  }

  setSigningMode(mode: 'just-me' | 'send-to-others'): void {
    this.signingMode = mode;
    this.recipientEmailControl.reset();
    this.recipientEmailError = '';
  }

  get isSubmitDisabled(): boolean {
    if (this.pendingDoc) {
      return !this.esignNameControl.value?.trim() || this.pendingLoader;
    }
    const nameEmpty = !this.esignNameControl.value?.trim();
    const fileEmpty = !this.uploadedFileName;
    if (this.signingMode === 'send-to-others') {
      const emailValue = this.recipientEmailControl.value?.trim() ?? '';
      const emailValid = !!emailValue && new RegExp(RegExpPatterns.EmailPattern).test(emailValue);
      return fileEmpty || !emailValid;
    }
    return nameEmpty || fileEmpty;
  }

  onSubmit(): void {
    if (this.pendingDoc) {
      this.submitPendingEsign();
      return;
    }
    if (!this.selectedFile) {
      this.uploadErrorMessage = 'Please select a file before submitting.';
      return;
    }
    if (this.signingMode === 'send-to-others') {
      this.shareToOthers();
      return;
    }
    if (!this.esignNameControl.value?.trim()) {
      this.uploadErrorMessage = 'Please enter signatory name.';
      return;
    }
    this.navigateToEsignPosition(this.selectedFile);
  }

  private submitPendingEsign(): void {
    this.pendingLoader = true;
    this.publicEsignService.exportSharedDocument(this.pendingDoc._id).subscribe({
      next: (blob: any) => {
        this.pendingLoader = false;
        if (!blob || !(blob instanceof Blob)) {
          this.errorMessage = 'Failed to retrieve document for signing.';
          this.showErrorToaster = true;
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        const popup = this.modalService.open(EsignPositonComponent, {
          backdrop: 'static',
          keyboard: false,
          centered: true,
          fullscreen: true,
        });
        if (popup.componentInstance) {
          popup.componentInstance.pdfSrc = objectUrl;
          popup.componentInstance.isShowUsers = false;
          popup.result
            .then((signPositions: any) => {
              URL.revokeObjectURL(objectUrl);
              if (!signPositions) return;
              this.verifyLoader = true;
              const payload = {
                sharedDocumentId: this.pendingDoc._id,
                signPositions,
                esignName: this.esignNameControl.value?.trim() ?? '',
              };
              this.publicEsignService.submitSharedEsign(payload).subscribe({
                next: (res: any) => {
                  if (res?.data) {
                    submitForm(res.data, (err: any) => {
                      this.verifyLoader = false;
                      if (err) {
                        this.errorMessage = err.message ?? 'E-sign failed or timed out.';
                        this.showErrorToaster = true;
                        return;
                      }
                      this.close();
                      this.successMessage = 'Document e-signed successfully';
                      this.showSuccessToaster = true;
                      this.authService.triggerCreditsRefresh();
                      this.overlayService.notifySuccess();
                      this.router.navigate([RouteConstants.ESign]);
                    });
                  } else {
                    this.verifyLoader = false;
                    this.errorMessage = 'Failed to process e-sign document.';
                    this.showErrorToaster = true;
                  }
                },
                error: (error: any) => {
                  this.verifyLoader = false;
                  this.errorMessage = error?.error?.error?.message || 'Failed to submit e-sign';
                  this.showErrorToaster = true;
                },
              });
            })
            .catch(() => {
              URL.revokeObjectURL(objectUrl);
            });
        }
      },
      error: (error: any) => {
        this.pendingLoader = false;
        this.errorMessage = error?.error?.error?.message || 'Failed to export document';
        this.showErrorToaster = true;
      },
    });
  }

  private shareToOthers(): void {
    const emailValue = this.recipientEmailControl.value?.trim() ?? '';
    const emailRegex = new RegExp(RegExpPatterns.EmailPattern);
    if (!emailValue) {
      this.recipientEmailError = 'Please enter recipient email.';
      return;
    }
    if (!emailRegex.test(emailValue)) {
      this.recipientEmailError = 'Please enter a valid email address.';
      return;
    }

    this.shareLoader = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile!, this.selectedFile!.name);
    formData.append('recipientEmails', JSON.stringify([emailValue]));
    if (this.deductFromOwner) {
      formData.append('deductFromOwner', 'true');
    }
    this.publicEsignService.shareToOthers(formData).subscribe({
      next: () => {
        this.shareLoader = false;
        this.close();
        this.successMessage = 'Document sent to recipients successfully';
        this.showSuccessToaster = true;
        this.authService.triggerCreditsRefresh();
        this.router.navigate([RouteConstants.ESign]);
      },
      error: (error: any) => {
        this.shareLoader = false;
        this.errorMessage = error?.error?.error?.message || 'Failed to share document';
        this.showErrorToaster = true;
      },
    });
  }

  private navigateToEsignPosition(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      const pdfSrc = reader.result as string;
      const popup = this.modalService.open(EsignPositonComponent, {
        backdrop: 'static',
        keyboard: false,
        centered: true,
        fullscreen: true,
      });
      if (popup.componentInstance) {
        popup.componentInstance.pdfSrc = pdfSrc;
        popup.componentInstance.isShowUsers = false;
        popup.result
          .then((signPositions: any) => {
            if (signPositions) this.submitEsignDocument(signPositions);
          })
          .catch(() => { });
      }
    };
    reader.onerror = () => { this.uploadErrorMessage = 'Error reading the file.'; };
    reader.readAsDataURL(file);
  }

  private submitEsignDocument(signPositions: any[]): void {
    this.verifyLoader = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile!, this.selectedFile!.name);
    formData.append('signPositions', JSON.stringify(signPositions));
    formData.append('esignName', this.esignNameControl.value ?? '');
    this.publicEsignService.esignDocument(formData)?.subscribe({
      next: (res: any) => {
        if (res?.data) {
          submitForm(res.data, (err: any) => {
            this.verifyLoader = false;
            if (err) {
              this.errorMessage = err.message ?? 'E-sign failed or timed out.';
              this.showErrorToaster = true;
              return;
            }
            this.close();
            this.successMessage = 'Document e-signed successfully';
            this.showSuccessToaster = true;
            this.authService.triggerCreditsRefresh();
            this.overlayService.notifySuccess();
            this.router.navigate([RouteConstants.ESign]);
          });
        } else {
          this.verifyLoader = false;
          this.errorMessage = 'Failed to process e-sign document.';
          this.showErrorToaster = true;
        }
      },
      error: (error: any) => {
        this.verifyLoader = false;
        this.errorMessage = error?.error?.error?.message || 'Failed to submit e-sign document';
        this.showErrorToaster = true;
      },
    });
  }

  closeToaster(type: 'success' | 'error'): void {
    if (type === 'success') { this.showSuccessToaster = false; this.successMessage = ''; }
    else { this.showErrorToaster = false; this.errorMessage = ''; }
  }
}
